/**
 * AnimatedSection — entrance-animation wrapper (opacity + translate + optional
 * scale) used across the app's pages and cards.
 *
 * CSS-driven (no framer-motion): the start offset/scale/duration/delay are passed
 * to the `.animated-section` keyframe via CSS custom properties. RTL flips the
 * horizontal direction. Honors prefers-reduced-motion (renders a plain div, and
 * the keyframe is disabled in CSS as a belt-and-suspenders fallback).
 *
 * @layer-1 — shared component.
 */
import { useReducedMotion } from "@/shared/hooks/useReducedMotion";
import type { ReactNode, CSSProperties } from "react";
import { cn } from "@/shared/lib/utils";

type Direction = "up" | "down" | "left" | "right";

interface AnimatedSectionProps {
  children: ReactNode;
  delay?: number;
  duration?: number;
  direction?: Direction;
  /** Add a subtle scale (0.97 → 1) matching the floatIn CSS keyframe. */
  withScale?: boolean;
  className?: string;
}

const DURATION_FALLBACK = 0.5;

export function AnimatedSection({
  children,
  delay = 0,
  duration = DURATION_FALLBACK,
  direction = "up",
  withScale = false,
  className,
}: AnimatedSectionProps) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  // Read direction at render — the document dir is stable for the page lifetime
  // and this matches getEffectiveDir() usage elsewhere (no effect/state needed).
  const isRTL = document.documentElement.dir === "rtl";
  const shouldFlip = isRTL && (direction === "left" || direction === "right");
  const fromX =
    direction === "left"
      ? shouldFlip ? "24px" : "-24px"
      : direction === "right"
        ? shouldFlip ? "-24px" : "24px"
        : "0px";
  const fromY = direction === "up" ? "24px" : direction === "down" ? "-24px" : "0px";

  const style = {
    "--as-x": fromX,
    "--as-y": fromY,
    "--as-scale": withScale ? "0.97" : "1",
    "--as-duration": `${duration}s`,
    "--as-delay": `${delay}s`,
  } as CSSProperties;

  return (
    <div className={cn("animated-section", className)} style={style}>
      {children}
    </div>
  );
}
